/**
 * Checks whether a section has at least one system field or custom field.
 * @param {{ systemFields?: Object, customFields?: Object }} [section] Section fields.
 * @returns {boolean} `true` when the section has any field, otherwise `false`.
 */
export const hasFields = ({ systemFields = {}, customFields = {} } = {}) =>
  Object.keys({ ...systemFields, ...customFields }).length > 0

/**
 * Checks whether a section has at least one field the user can edit.
 * @param {{ systemFields?: Object, customFields?: Object }} [section] Section fields.
 * @returns {boolean} `true` when at least one field is not `disabled`, otherwise `false`.
 */
export const hasEditableFields = ({ systemFields = {}, customFields = {} } = {}) =>
  Object.values({ ...systemFields, ...customFields }).some((field) => !field?.disabled)

/**
 * Checks whether the user can view a section (the fields endpoint only returns viewable fields).
 * @param {{ systemFields?: Object, customFields?: Object }} [section] Section fields.
 * @returns {boolean} `true` when the section has any field, otherwise `false`.
 */
export const canViewSection = (section) => hasFields(section)

/**
 * Resolves the render state of a list section card.
 * A viewable section MUST NOT be hidden only because it has no items.
 * @param {{ canView: boolean, canEdit: boolean, itemsCount?: number }} params
 * @returns {'hidden' | 'empty' | 'list'} `hidden` without view permission, `empty` without
 *   items and edit permission, otherwise `list`.
 */
export const getListSectionState = ({ canView, canEdit, itemsCount = 0 }) => {
  if (!canView) return 'hidden'
  if (!itemsCount && !canEdit) return 'empty'

  return 'list'
}
